import PropTypes from 'prop-types';
import React from 'react';
import { Link } from 'react-router-dom';
import { format } from 'url';
import config from 'config';
import Button from '@mulesoft/anypoint-components/lib/Button';
import Tooltip from '@mulesoft/anypoint-components/lib/Tooltip';
import Icon from '@mulesoft/anypoint-icons/lib/Icon';
import { Table } from '@mulesoft/exchange-ui-components';
import {
  dateFormat,
  ScanConfigurationPropType,
  ScannerStatusEnum
} from '~/components/AgentScanners/types/commonTypes';
import { getPath } from '~/utils/routes';
import {
  formatRelativeTime,
  getStatsFromSummary
} from '~/components/AgentScanners/utils/utils';
import { formatDate } from '~/utils/formatDate';
import ScanConfigurationActions from '../ScanConfigurationActions';
import Pagination from './Pagination';
import Header from './Header';
import Status from './Status';
import StatusRun from './StatusRun';
import styles from './ScanConfigurationsList.css';

const getHeaders = () => {
  const headers = [
    {
      key: 'status',
      label: 'Scanner Status'
    },
    {
      key: 'name',
      label: 'Scanner Name'
    },
    {
      key: 'target',
      label: 'Agent Provider'
    },
    {
      key: 'lastCompletedRun',
      label: 'Last Completed Run'
    },
    {
      key: 'lastRunStatus',
      label: 'Last Run Status'
    },
    {
      key: 'nextRun',
      label: 'Next Run'
    },
    {
      key: 'totalAgents',
      label: (
        <div data-test-id="table-th-total-agents-col">
          <Tooltip
            content="Total agents managed by this scanner, including successful imports and updates"
            position="bottom"
            className={styles.totalAgentsTooltip}
            testId="total-agents-header-tooltip"
          >
            <span className={styles.totalAgentsTableHeaderCol}>
              Total Agents
            </span>
            <Icon name="info-small" size={19} className={styles.infoIcon} />
          </Tooltip>
        </div>
      )
    },
    {
      key: 'actions'
    }
  ];

  return headers;
};

function renderEmptyState() {
  const emptyImageURL = format({
    ...config.images.uri,
    pathname: 'empty-list-agent-scanner.png'
  });
  const addScannerPath = getPath('agentScannersAdd');

  return (
    <div className={styles.emptyState} data-test-id="empty-state">
      <img
        src={emptyImageURL}
        alt="Empty Agent Scanners"
        className={styles.emptyStateImage}
      />
      <p className={styles.emptyStateDescription}>
        Add an agent scanner to discover and import agents from external systems
        into a single registry. Transform fragmented assets into a governed,
        enterprise-ready central asset repository.
        <a
          href={config.docsLinks.supportedScanners}
          className={styles.emptyStateLinkAnchor}
          target="_blank"
          rel="noopener noreferrer"
        >
          View supported scanners.
        </a>
      </p>
      <div className={styles.emptyStateButtonGroup}>
        <Button
          testId="empty-state-import-agentforce-button"
          kind="secondary"
          onClick={() => {
            // protocol and host come from server configuration (config/default.js),
            // not from user input. These are trusted values set at build/deploy time.
            const { protocol, host } = config.platform.uri;
            const settingsURL = new URL(
              '/exchange/settings/',
              `${protocol}://${host}`
            );

            settingsURL.searchParams.set('tab', 'asset-import');
            window.open(
              settingsURL.toString(),
              '_blank',
              'noopener,noreferrer'
            );
          }}
        >
          Import Agentforce Agents
        </Button>
        <Link to={addScannerPath}>
          <Button testId="empty-state-add-scanner-button" kind="primary">
            Add Scanner
          </Button>
        </Link>
      </div>
    </div>
  );
}

/**
 * @param {Object} scanConfig - Configuration object for scanning - PropTypes.arrayOf(ScanConfigurationPropType)
 * @param {Function} getPathFn - Function to get path
 * @param {Function} onClick - Click handler function
 * @param {boolean} isDisabled - Whether the component is disabled
 * @returns {*} - Name component
 */

function scanConfigurationName(
  scanConfig,
  getPathFn,
  onClick,
  isDisabled = false
) {
  const path = getPathFn && getPathFn('scanConfigHome', scanConfig);

  const handleClick = (e) => {
    e.preventDefault();
    if (onClick) {
      onClick(scanConfig);
    }
  };

  if (isDisabled) {
    return (
      <div className={styles.disabled}>
        <span>{scanConfig?.name || ''}</span>
      </div>
    );
  }

  // If we have an onClick handler, make it clickable even without a path
  if (onClick) {
    return (
      <div className={styles.name}>
        <button
          type="button"
          className={styles.nameButton}
          onClick={handleClick}
        >
          <span className="visually-hidden">Go to Scan Configuration</span>
          {scanConfig.name}
        </button>
      </div>
    );
  }

  // Render Link if path is available
  if (path) {
    return (
      <div className={styles.name}>
        <Link to={path}>
          <span className="visually-hidden">Go to Scan Configuration</span>
          {scanConfig.name}
        </Link>
      </div>
    );
  }

  // Fallback: just text, not clickable
  return (
    <div className={styles.name}>
      <span>{scanConfig.name}</span>
    </div>
  );
}

function scanConfigurationTargetSystem(scanConfig, isDisabled = false) {
  const targetSystemDescription =
    scanConfig.connection?.targetSystem?.name || '-';

  if (isDisabled) {
    return <div className={styles.disabled}>{targetSystemDescription}</div>;
  }

  return <div className={styles.text}>{targetSystemDescription}</div>;
}

function renderDateWithTooltip(dateString, testIdPrefix, isDisabled = false) {
  if (!dateString) {
    return (
      <span
        data-test-id={`scan-config-${testIdPrefix}`}
        className={isDisabled ? styles.disabled : ''}
      >
        -
      </span>
    );
  }

  const relativeTime = formatRelativeTime(dateString);
  const formattedDate = formatDate(dateString, dateFormat);

  return (
    <Tooltip
      content={formattedDate}
      position="top"
      testId={`scan-config-${testIdPrefix}-tooltip`}
    >
      <span
        data-test-id={`scan-config-${testIdPrefix}`}
        className={isDisabled ? styles.disabled : ''}
      >
        {relativeTime}
      </span>
    </Tooltip>
  );
}

function renderLastCompletedRun(dateString, isDisabled) {
  return renderDateWithTooltip(dateString, 'last-run', isDisabled);
}

function renderNextRun(dateString, isDisabled) {
  return renderDateWithTooltip(dateString, 'next-run', isDisabled);
}

function calculateTotalAgents(scanConfig) {
  const stats = getStatsFromSummary(scanConfig.lastSuccessfulRunSummary);

  if (!stats) {
    console.warn('Stats object not found in lastSuccessfulRunSummary');

    return 0;
  }

  try {
    // Sum added + updated + unchanged
    const added = Number(stats.added) || 0;
    const updated = Number(stats.updated) || 0;
    const unchanged = Number(stats.unchanged) || 0;

    return added + updated + unchanged;
  } catch (error) {
    console.error(
      'Failed to calculate total agents from lastSuccessfulRunSummary JSON:',
      error
    );

    return 0;
  }
}

function mapScanConfigurations(
  scanConfigurations,
  getPathFn,
  onClick,
  onRunClick,
  onAbortClick,
  onEditClick,
  onDeleteClick,
  organizationId
) {
  return scanConfigurations.map((scanConfig) => {
    const isDisabled =
      scanConfig.state &&
      scanConfig.state.toLowerCase() === ScannerStatusEnum.DELETING.key;
    const scanConfigRow = {
      status: <Status status={scanConfig.state} />,
      name: scanConfigurationName(scanConfig, getPathFn, onClick, isDisabled),
      target: scanConfigurationTargetSystem(scanConfig, isDisabled),
      lastCompletedRun: renderLastCompletedRun(
        scanConfig.lastSuccessfulRunAt,
        isDisabled
      ),
      lastRunStatus: (
        <StatusRun
          status={scanConfig.lastRunStatus}
          statusDetail={scanConfig.lastRunStatusDetail || ''}
          isDisabled={isDisabled}
        />
      ),
      nextRun: renderNextRun(scanConfig.nextRunAt, isDisabled),
      totalAgents: (
        <div className={isDisabled ? styles.disabled : styles.text}>
          {calculateTotalAgents(scanConfig)}
        </div>
      ),
      actions: (
        <ScanConfigurationActions
          onRun={() => onRunClick(scanConfig, organizationId)}
          onAbort={() => onAbortClick(scanConfig, organizationId)}
          onEdit={() => onEditClick(scanConfig.id)}
          onDelete={() => onDeleteClick(scanConfig)}
          scannerStatus={scanConfig.state}
          isMenuDisabled={isDisabled}
        />
      )
    };

    return scanConfigRow;
  });
}

const ScanConfigurationsList = ({
  id = '',
  scanConfigurations = [],
  scannersError = null,
  organizationId = null,
  onLoadScanConfigurations = () => {},
  onScanConfigurationClick = () => {},
  currentPage = 1,
  itemsPerPage = 3,
  onPageChange = () => {},
  onRunNowClick = () => {},
  onAbortClick = () => {},
  onEditScannerClick = () => {},
  onDeleteScannerClick = () => {},
  agentCapacity = null
}) => {
  if (scannersError) {
    return (
      <div className={styles.errorState}>
        <h3>Error loading scanners</h3>
        <p>An error occurred while loading the scanners.</p>
        <Button
          kind="primary"
          onClick={() => onLoadScanConfigurations(organizationId)}
        >
          Retry
        </Button>
      </div>
    );
  }

  // Calculate pagination
  const totalItems = scanConfigurations.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedScanConfigurations = scanConfigurations.slice(
    startIndex,
    endIndex
  );

  return (
    <div>
      <Header
        emptyState={scanConfigurations.length === 0}
        agentCapacity={agentCapacity}
      />
      {scanConfigurations.length > 0 ? (
        <div>
          <div
            id={id}
            className={styles.scanConfigurationsList}
            data-test-id="scan-configurations-list"
          >
            <Table
              caption="Scan Configurations results"
              testId="scanConfigurations"
              className={styles.table}
              headers={getHeaders()}
              rows={mapScanConfigurations(
                paginatedScanConfigurations,
                getPath,
                onScanConfigurationClick,
                onRunNowClick,
                onAbortClick,
                onEditScannerClick,
                onDeleteScannerClick,
                organizationId
              )}
              emptyMessage="We couldn't find results that match your criteria."
            />
          </div>

          {totalPages > 1 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={onPageChange}
              itemsPerPage={itemsPerPage}
              totalItems={totalItems}
              testId="scan-configurations-pagination"
            />
          )}
        </div>
      ) : (
        renderEmptyState()
      )}
    </div>
  );
};

ScanConfigurationsList.propTypes = {
  id: PropTypes.string,
  scanConfigurations: PropTypes.arrayOf(ScanConfigurationPropType),
  scannersError: PropTypes.object,
  organizationId: PropTypes.string,
  onLoadScanConfigurations: PropTypes.func,
  onScanConfigurationClick: PropTypes.func,
  currentPage: PropTypes.number,
  itemsPerPage: PropTypes.number,
  onPageChange: PropTypes.func,
  onRunNowClick: PropTypes.func,
  onAbortClick: PropTypes.func,
  onEditScannerClick: PropTypes.func,
  onDeleteScannerClick: PropTypes.func,
  agentCapacity: PropTypes.shape({
    used: PropTypes.number,
    total: PropTypes.number
  })
};

export { ScanConfigurationsList };

export default ScanConfigurationsList;
